import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeftIcon, ClockIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import axiosInstance from '../../api/axiosInstance'
import Departamentos from '../../assets/data/departamentos.json'
import Ciudades from '../../assets/data/ciudades.json'
import { tiposDeDocumento, formaPago, metodoPago } from '../../assets/data/arrays'
import CotizacionForm from '../forms/CotizacionForm'
import { transformCotizacionData } from '../../utils/cotizacionTransform'
import toast from 'react-hot-toast'
import LoadingSpinner from '../LoadingSpinner'
import { Button } from '../ui'

const CotizacionUpdate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [cotizacionDetail, setCotizacionDetail] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false); // Reservado para uso futuro
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<any>(null); // Reservado para uso futuro
  const navigate = useNavigate();

  const fetchCotizacionDetail = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/cotizacion/${id}`);
      setCotizacionDetail(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error obteniendo el detalle de la cotización:', error);
      setError('Error al cargar los datos de la cotización');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCotizacionDetail();
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      const transformedData = transformCotizacionData(data);
      await axiosInstance.put(`/cotizacion/${id}`, transformedData);
      toast.success('Cotización actualizada correctamente 🚀', {
        position: 'top-center',
        duration: 3000,
      });
      // Recargar los datos actualizados para que se reflejen en el formulario
      await fetchCotizacionDetail();
    } catch (err) {
      console.error('Error actualizando cotización:', err);
      setError('Error al actualizar la cotización. Por favor, intente nuevamente.');
      toast.error('Error al actualizar la cotización', {
        position: 'top-center',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el número de cotización desde cualquier fuente
  function getNumeroCotizacion(cotizacionDetail: any) {
    return (
      cotizacionDetail.cotizaciones_id ||
      cotizacionDetail.cotizacion_id ||
      cotizacionDetail.id ||
      (Array.isArray(cotizacionDetail.meta_data)
        ? (cotizacionDetail.meta_data.find((m: any) => m.key === 'cotizacion_id')?.value ||
           cotizacionDetail.meta_data.find((m: any) => m.key === 'id')?.value)
        : '')
    );
  }

  // Función para obtener la fecha de última actualización desde cualquier fuente
  function getFechaActualizacion(cotizacionDetail: any) {
    return (
      cotizacionDetail.date_modified ||
      cotizacionDetail.date_updated ||
      (Array.isArray(cotizacionDetail.meta_data)
        ? (cotizacionDetail.meta_data.find((m: any) => m.key === 'date_modified')?.value ||
           cotizacionDetail.meta_data.find((m: any) => m.key === 'date_updated')?.value)
        : '')
    );
  }

  if (!cotizacionDetail) {
    return <div className="flex justify-center items-center min-h-[200px]"><LoadingSpinner size="large" /></div>;
  }

  return (
    <div className="p-10 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-start gap-4 flex-1">
          {/* Botón de retroceso mejorado */}
          <Button
            onClick={() => navigate('/cotizaciones')}
            variant="ghost"
            size="sm"
            className="mt-1"
            title="Volver a cotizaciones"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          
          <div className="flex-1">
            {/* Título con icono */}
            <div className="flex items-center gap-3 mb-2">
              <PencilSquareIcon className="h-7 w-7 text-custom-blue flex-shrink-0" />
            <h3>
              Editar Cotización #{getNumeroCotizacion(cotizacionDetail)}
            </h3>
            </div>
            
            {/* Información adicional */}
            {getFechaActualizacion(cotizacionDetail) && (
              <div className="flex items-center gap-2 text-sm text-gray-600 ml-10">
                <ClockIcon className="h-4 w-4 text-custom-blue flex-shrink-0" />
                <span>
                  Última actualización: <span className="font-medium">{new Date(getFechaActualizacion(cotizacionDetail)).toLocaleString()}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <CotizacionForm
        initialData={cotizacionDetail}
        mode="edit"
        onSubmit={handleSubmit}
        onFormChange={setFormData}
        tiposDeDocumento={tiposDeDocumento}
        departamentos={Departamentos}
        ciudades={Ciudades}
        formaPago={formaPago}
        metodoPago={metodoPago}
      />
    </div>
  );
};

export default CotizacionUpdate; 